import { useState } from 'react';
import Button from '@/components/Button';
import type { RunPayCycleOptions } from '@/modules/payroll/hooks/usePayRunCycleLogs';

const OPTION_FIELDS: {
  key: keyof RunPayCycleOptions;
  label: string;
  hint: string;
}[] = [
  {
    key: 'shouldSave',
    label: 'Save results',
    hint: 'Persists pay runs and exports. Off = dry run.',
  },
  {
    key: 'allOrNothingMode',
    label: 'All or nothing',
    hint: 'Abort the entire run if any worker fails.',
  },
  {
    key: 'attemptToFixErrors',
    label: 'Auto-fix issues',
    hint: 'Let the coordinator patch obvious data mismatches.',
  },
];

const DEFAULT_OPTIONS: RunPayCycleOptions = {
  shouldSave: true,
  allOrNothingMode: false,
  attemptToFixErrors: true,
};

type RunPayCycleModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (options: RunPayCycleOptions) => Promise<void>;
};

const RunPayCycleModal = ({
  isOpen,
  onClose,
  onSubmit,
}: RunPayCycleModalProps) => {
  const [options, setOptions] = useState<RunPayCycleOptions>(DEFAULT_OPTIONS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) {
    return null;
  }

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit(options);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Unable to trigger pay run.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4 py-8"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="run-pay-cycle-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h3
          id="run-pay-cycle-title"
          className="text-xl font-bold text-gray-800"
        >
          Run pay cycle
        </h3>
        <p className="mt-2 text-sm text-gray-600">
          The run is queued and uses the backend&apos;s default pay window.
          You&apos;ll get an email when it finishes.
        </p>

        <div className="mt-5 flex flex-col gap-4">
          {OPTION_FIELDS.map((field) => (
            <label
              key={field.key}
              className="flex cursor-pointer items-start justify-between gap-4"
            >
              <span>
                <span className="block text-sm font-medium text-gray-900">
                  {field.label}
                </span>
                <span className="block text-xs text-gray-500">
                  {field.hint}
                </span>
              </span>
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-[#03406A]"
                checked={options[field.key]}
                disabled={isSubmitting}
                onChange={(event) =>
                  setOptions((current) => ({
                    ...current,
                    [field.key]: event.target.checked,
                  }))
                }
              />
            </label>
          ))}
        </div>

        {error && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <Button
            variant="outline"
            className="py-2 text-sm"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="py-2 text-sm"
            onClick={() => void handleSubmit()}
            isLoading={isSubmitting}
          >
            Run pay cycle
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RunPayCycleModal;
